import React, { useCallback, useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { isFavoriteAsset, toggleFavoriteAsset } from '@/shared/lib/storage';
import { Icon } from '@/shared/ui/Icon';
import { IconButton, type IconButtonSize } from '@/shared/ui/IconButton';

import type { FavoriteAsset } from '@/entities/asset';

type FavoriteButtonProps = {
  assetId: string;
  assetName: string;
  assetType: string;
  assetFqn: string;
  projectId?: string;
  experimentId?: string;
  experimentName?: string;
  size?: IconButtonSize;
  iconSize?: number;
  className?: string;
};

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  assetId,
  assetName,
  assetType,
  assetFqn,
  projectId,
  experimentId,
  experimentName,
  size = 'regular',
  iconSize = 16,
  className,
}) => {
  const [isFavorite, setIsFavorite] = useState(false);

  const updateFavoriteState = useCallback(() => {
    setIsFavorite(isFavoriteAsset(assetId));
  }, [assetId]);

  useEffect(() => {
    updateFavoriteState();
  }, [updateFavoriteState]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateFavoriteState();
      }
    };

    const handleFocus = () => {
      updateFavoriteState();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [updateFavoriteState]);

  const handleToggleFavorite = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }

      const favoriteAsset: FavoriteAsset = {
        id: assetId,
        name: assetName,
        type: assetType,
        fqn: assetFqn,
        addedAt: dayjs().toISOString(),
        ...(projectId && { projectId }),
        ...(experimentId && { experimentId }),
        ...(experimentName && { experimentName }),
      };

      const newFavoriteState = toggleFavoriteAsset(favoriteAsset);
      setIsFavorite(newFavoriteState);
    },
    [assetId, assetName, assetType, assetFqn, projectId, experimentId, experimentName]
  );

  const starIcon = <Icon name={isFavorite ? 'star-filled' : 'star'} size={iconSize} strokeWidth={1.5} />;

  return (
    <IconButton
      size={size}
      onClick={handleToggleFavorite}
      className={className}
      tooltip={isFavorite ? '즐겨찾기 제거' : '즐겨찾기 추가'}>
      {starIcon}
    </IconButton>
  );
};
